import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { search, type SearchHit } from '../content/search';

// Top-nav search: a typeahead dropdown of the top hits as you type, plus Enter
// to open the full /search results page. Keyboard: Up/Down to move, Enter to
// open the highlighted hit (or the results page), Esc to close.
const DROPDOWN_LIMIT = 7;

export default function SearchBox() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setHits([]);
      return;
    }
    setHits(search(term, DROPDOWN_LIMIT));
    setActive(-1);
  }, [q]);

  // Close the dropdown on an outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function goToResults() {
    const term = q.trim();
    if (!term) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(term)}`);
  }

  function goToHit(hit: SearchHit) {
    setOpen(false);
    setQ('');
    navigate(`/docs/${hit.slug}`);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, hits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && active >= 0 && active < hits.length) goToHit(hits[active]);
      else goToResults();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="app-search" ref={rootRef}>
      <form
        className="app-search-form"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          goToResults();
        }}
      >
        <input
          type="search"
          className="app-search-input"
          placeholder="Search docs…"
          aria-label="Search documentation"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => q.trim().length >= 2 && setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
        />
      </form>
      {open && q.trim().length >= 2 && (
        <div className="app-search-dropdown" role="listbox">
          {hits.length === 0 ? (
            <div className="app-search-empty">No matches</div>
          ) : (
            <>
              {hits.map((hit, i) => (
                <button
                  type="button"
                  key={hit.slug}
                  role="option"
                  aria-selected={i === active}
                  className={`app-search-hit${i === active ? ' active' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => goToHit(hit)}
                >
                  <span className="app-search-hit-title">{hit.title}</span>
                  <span className="app-search-hit-meta">
                    {hit.platformLabel}
                    {hit.groupLabel ? ` · ${hit.groupLabel}` : ''}
                  </span>
                </button>
              ))}
              <button type="button" className="app-search-all" onClick={goToResults}>
                See all results for “{q.trim()}” →
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
